/**
 * Upsell Bundle — fixed-price tier display + variant sync.
 * Reuses wrapp-hdt-pr-frm-bundle / hdt-bundle-product for ATC & totals.
 * Display-only: real discounts come from Shopify automatic discounts / Functions.
 */
import { formatMoney } from 'global';

if (!customElements.get('hdt-upsell-bundle')) {
  const VARIANT_CHANGE = 'variant:change';
  const BUNDLE_TOTAL = 'update:bundle:total';

  function moneyFormat() {
    return (
      (window.themeHDN && window.themeHDN.settings && window.themeHDN.settings.currencyFormat) ||
      (window.themeHDN && window.themeHDN.settings && window.themeHDN.settings.moneyFormat) ||
      '{{amount}}'
    );
  }

  function formatCents(cents) {
    const value = Number(cents) || 0;
    try {
      return formatMoney(value, moneyFormat());
    } catch (e) {
      return String(value);
    }
  }

  function getTiers() {
    const cfg = window.themeHDN && window.themeHDN.upsellBundle;
    if (!cfg || !cfg.enabled) return [];
    return (cfg.tiers || [])
      .filter((t) => t && t.qty > 0 && t.price > 0)
      .slice()
      .sort((a, b) => a.qty - b.qty);
  }

  function pickTier(count, tiers) {
    let match = null;
    for (const tier of tiers) {
      if (count >= tier.qty) match = tier;
    }
    return match;
  }

  class HdtUpsellBundle extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.form = this.querySelector('form');
      this.totalNode = this.querySelector('[update-total]');
      this.originalEl = this.querySelector('[data-ub-original] .hdt-money');
      this.discountedEl = this.querySelector('[data-ub-discounted] .hdt-money');
      this.discountedWrap = this.querySelector('[data-ub-discounted]');
      this.originalWrap = this.querySelector('[data-ub-original]');
      this.savingsWrap = this.querySelector('[data-ub-savings]');
      this.saveAmountEl = this.querySelector('[data-ub-save-amount]');
      this.savePercentEl = this.querySelector('[data-ub-save-percent]');
      this.tierCards = [...this.querySelectorAll('[data-ub-tier]')];
      this.showSavings = this.getAttribute('data-show-savings') === 'true';
      this.showPercentage = this.getAttribute('data-show-percentage') === 'true';

      this._onBundleTotal = this.updatePricing.bind(this);
      this._onVariantChange = this.onMainVariantChange.bind(this);

      if (this.form) {
        this.form.addEventListener(BUNDLE_TOTAL, this._onBundleTotal);
      }

      const mainFormId = this.getAttribute('data-main-form');
      this.mainForm = mainFormId ? document.forms[mainFormId] : null;
      if (this.mainForm) {
        this.mainForm.addEventListener(VARIANT_CHANGE, this._onVariantChange);
      }

      this.addEventListener('change', () => {
        requestAnimationFrame(() => this.updatePricing());
      });

      this.updatePricing();
    }

    disconnectedCallback() {
      if (this.form && this._onBundleTotal) {
        this.form.removeEventListener(BUNDLE_TOTAL, this._onBundleTotal);
      }
      if (this.mainForm && this._onVariantChange) {
        this.mainForm.removeEventListener(VARIANT_CHANGE, this._onVariantChange);
      }
      this._bound = false;
    }

    getCheckedCount() {
      return this.querySelectorAll('hdt-bundle-product.is-checked').length;
    }

    getOriginalTotal() {
      if (!this.totalNode) return 0;
      const compare = Number(this.totalNode.getAttribute('data-cpprice') || 0);
      const sale = Number(this.totalNode.getAttribute('data-price') || 0);
      return compare > sale ? compare : sale;
    }

    updatePricing() {
      const tiers = getTiers();
      const count = this.getCheckedCount();
      const original = this.getOriginalTotal();
      const tier = pickTier(count, tiers);

      let discounted = original;
      let savings = 0;
      let percent = 0;

      if (tier && original > 0 && count >= 2) {
        discounted = Math.min(tier.price * 100, original);
        savings = Math.max(0, original - discounted);
        percent = original > 0 ? Math.round((savings / original) * 100) : 0;
      }

      this.renderPrices(original, discounted, savings, percent, count >= 2 && savings > 0);
      this.renderTierCards(count, original, tiers);
      document.dispatchEvent(new CustomEvent('currency:update'));
    }

    renderPrices(original, discounted, savings, percent, showSave) {
      if (this.discountedWrap) {
        this.discountedWrap.classList.add('is-updating');
        requestAnimationFrame(() => {
          this.discountedWrap.classList.remove('is-updating');
        });
      }

      if (this.originalEl) {
        this.originalEl.textContent = formatCents(original);
      }
      if (this.originalWrap) {
        this.originalWrap.hidden = !(showSave && discounted < original);
      }
      if (this.discountedEl) {
        this.discountedEl.textContent = formatCents(discounted);
      }
      if (this.savingsWrap) {
        this.savingsWrap.hidden = !(this.showSavings && showSave);
        if (this.saveAmountEl) {
          this.saveAmountEl.textContent = formatCents(savings);
        }
        if (this.savePercentEl) {
          this.savePercentEl.textContent = this.showPercentage && percent > 0 ? ` (${percent}%)` : '';
        }
      }
    }

    renderTierCards(count, original, tiers) {
      const active = pickTier(count, tiers);

      this.tierCards.forEach((card) => {
        const qty = Number(card.getAttribute('data-qty') || 0);
        const priceMajor = Number(card.getAttribute('data-price') || 0);
        const isActive = active && active.qty === qty && count >= qty;
        card.classList.toggle('is-active', Boolean(isActive));
        card.setAttribute('aria-current', isActive ? 'true' : 'false');

        const badge = card.querySelector('[data-ub-tier-badge]');
        if (!badge) return;

        let badgeText = '';
        if (count > 0 && original > 0 && priceMajor > 0) {
          const projectedOriginal = Math.round((original / count) * qty);
          const projectedBundle = priceMajor * 100;
          const save = projectedOriginal - projectedBundle;
          if (save > 0) {
            const pct = Math.round((save / projectedOriginal) * 100);
            badgeText = `Save ${formatCents(save)}${pct > 0 ? ` · ${pct}%` : ''}`;
          }
        }

        if (badgeText) {
          badge.hidden = false;
          badge.textContent = badgeText;
        } else {
          badge.hidden = true;
          badge.textContent = '';
        }
      });
    }

    onMainVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;

      const mainItem = this.querySelector('[data-ub-main]');
      if (!mainItem) return;

      const select = mainItem.querySelector('[data-ub-main-select]');
      const hidden = mainItem.querySelector('[data-ub-main-input]');
      const priceWrap = mainItem.querySelector('[update-price]');
      const imageWrap = this.querySelector('[data-ub-main-image]');

      const price = variant.price;
      const compare = variant.compare_at_price || price;

      if (select) {
        const option = [...select.options].find((o) => String(o.value) === String(variant.id));
        if (option) {
          select.value = String(variant.id);
          select.setAttribute('data-price', String(price));
          select.setAttribute('data-cpprice', String(compare));
          option.setAttribute('data-price', String(price));
          option.setAttribute('data-cpprice', String(compare));
          option.selected = true;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (hidden) {
        hidden.value = String(variant.id);
        hidden.setAttribute('data-price', String(price));
        hidden.setAttribute('data-cpprice', String(compare));
      }

      if (priceWrap) {
        const cmp = priceWrap.querySelector('hdt-compare-at-price');
        const sale = priceWrap.querySelector('hdt-price .hdt-money');
        if (cmp) {
          const cmpMoney = cmp.querySelector('.hdt-money');
          if (cmpMoney) cmpMoney.textContent = formatCents(compare);
          cmp.hidden = !(compare > price);
        }
        if (sale) sale.textContent = formatCents(price);
      }

      if (imageWrap) {
        const img = imageWrap.querySelector('img');
        const featured = variant.featured_image;
        if (img && featured) {
          const raw = typeof featured === 'string' ? featured : featured.src;
          if (raw) {
            try {
              const url = new URL(raw, window.location.origin);
              img.src = url.pathname.includes('_') ? raw : raw.replace(/(\.\w+)(\?.*)?$/, '_160x$1$2');
            } catch (e) {
              img.src = raw;
            }
          }
        }
      }

      requestAnimationFrame(() => this.updatePricing());
    }
  }

  customElements.define('hdt-upsell-bundle', HdtUpsellBundle);
}
